import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import apiClient from "../utils/apiClient";
import authService from "../services/authService";
import logger from "../utils/logger";
import { BALANCES_REFRESH_EVENT } from "../utils/balanceEvents";

export const useBalances = () => {
  const { user, isLoggedIn, loading: authLoading } = useAuth();

  // State for real-time balances (feathers included for VIP status card updates)
  const [balances, setBalances] = useState({
    gold: 0,
    swp: 0,
    swpPlaythroughRequired: 0,
    feathers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Manual refresh function for when real-time doesn't work. Returns the fetched
  // balances (or null on failure) so callers can decide whether to retry.
  const refreshBalances = useCallback(async () => {
    if (!isLoggedIn || !user?.id) return null;

    try {
      logger.info("Manually refreshing balances for user:", user.id);
      const response = await apiClient.get(`/api/casino/balance/${user.id}`);
      const data = response.data.balances;

      const newBalances = {
        gold: parseFloat(data.fiat?.gold) || 0,
        swp: parseFloat(data.fiat?.swp) || 0,
        swpPlaythroughRequired:
          parseFloat(data.fiat?.swpPlaythroughRequired) || 0,
        feathers: parseInt(data.feathers) || 0,
      };

      setBalances(newBalances);
      logger.success("Balances manually refreshed:", newBalances);
      return newBalances;
    } catch (error) {
      logger.error("Failed to manually refresh balances:", error);
      return null;
    }
  }, [isLoggedIn, user?.id]);

  // Post-registration refresh. AuthContext fires BALANCES_REFRESH_EVENT once a
  // registration flow completes (the 10k Gold Coin grant is applied
  // server-side). The realtime subscription never fires for a balance that
  // already existed at subscribe time, so without this the header shows 0 GLD
  // until a manual reload. We refetch on the signal and retry once, because the
  // grant / players row can lag the SIGNED_IN event by a moment.
  useEffect(() => {
    if (!isLoggedIn || !user?.id) return;

    let cancelled = false;
    let retryTimer = null;

    const handleRefresh = async () => {
      const result = await refreshBalances();
      // If nothing landed yet (still fully empty), give the grant a moment and
      // try once more. Fail-safe: a failed/again-empty retry just leaves the
      // existing balance in place.
      const empty =
        !result ||
        ((Number(result.gold) || 0) === 0 && (Number(result.swp) || 0) === 0);
      if (!cancelled && empty) {
        retryTimer = setTimeout(() => {
          if (!cancelled) refreshBalances();
        }, 1500);
      }
    };

    window.addEventListener(BALANCES_REFRESH_EVENT, handleRefresh);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener(BALANCES_REFRESH_EVENT, handleRefresh);
    };
  }, [isLoggedIn, user?.id, refreshBalances]);

  // Load initial balances and set up real-time subscription
  useEffect(() => {
    // Wait for auth to finish loading before making any decisions
    if (authLoading) {
      return;
    }

    if (!isLoggedIn || !user?.id) {
      setBalances({ gold: 0, swp: 0, swpPlaythroughRequired: 0, feathers: 0 });
      setLoading(false);
      return;
    }

    // Load initial balances from database
    const loadInitialBalances = async () => {
      try {
        logger.info("Loading initial balances for user:", user.id);

        const response = await apiClient.get(`/api/casino/balance/${user.id}`);
        const data = response.data.balances;

        logger.info("Balance data loaded:", data);

        const newBalances = {
          gold: parseFloat(data.fiat?.gold) || 0,
          swp: parseFloat(data.fiat?.swp) || 0,
          swpPlaythroughRequired:
            parseFloat(data.fiat?.swpPlaythroughRequired) || 0,
          feathers: parseInt(data.feathers) || 0,
        };

        setBalances(newBalances);
      } catch (error) {
        logger.error("useBalances: Error loading initial balances", error);
        setError(error.message || "Failed to load initial balances");
        // Fallback to default balances
        setBalances({
          gold: 0,
          swp: 0,
          swpPlaythroughRequired: 0,
          feathers: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialBalances();

    // Set up real-time subscription with proper auth
    const setupSubscription = async () => {
      try {
        // Get session from authService
        const { session } = await authService.getSession();

        if (!session?.access_token) {
          logger.warn(
            "useBalances: No access token available for real-time subscription",
          );
          return null;
        }

        // Ensure Supabase client has the current session
        await supabase.auth.setSession(session);

        // Create channel with proper authentication
        const subscription = supabase
          .channel(`balance-changes-${user.id}`, {
            config: {
              broadcast: { self: true },
              presence: { key: user.id },
            },
          })
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "players",
              filter: `id=eq.${user.id}`,
            },
            (payload) => {
              logger.info("useBalances: Real-time balance update received", {
                userId: user.id,
                eventType: payload.eventType,
                oldBalances: {
                  gold: payload.old?.fiat_gold_balance,
                  swp: payload.old?.fiat_swp_balance,
                  feathers: payload.old?.feathers_balance,
                },
                newBalances: {
                  gold: payload.new.fiat_gold_balance,
                  swp: payload.new.fiat_swp_balance,
                  feathers: payload.new.feathers_balance,
                },
              });

              // Update balances in real-time
              const newBalances = {
                gold: parseFloat(payload.new.fiat_gold_balance) || 0,
                swp: parseFloat(payload.new.fiat_swp_balance) || 0,
                swpPlaythroughRequired:
                  parseFloat(payload.new.fiat_swp_playthrough_required) || 0,
                feathers: parseInt(payload.new.feathers_balance) || 0,
              };

              logger.success(
                "useBalances: Balances updated via real-time",
                newBalances,
              );
              setBalances(newBalances);
            },
          )
          .subscribe((status) => {
            logger.info("useBalances: Real-time subscription status:", status);
            if (status === "CHANNEL_ERROR") {
              logger.error(
                "useBalances: Real-time subscription failed - check RLS policies and auth",
              );
            } else if (status === "SUBSCRIBED") {
              logger.success("useBalances: Real-time subscription active");
            }
          });

        return subscription;
      } catch (error) {
        logger.error(
          "useBalances: Failed to setup real-time subscription:",
          error,
        );
        return null;
      }
    };

    let subscription = null;
    let isMounted = true;

    setupSubscription().then((sub) => {
      if (isMounted && sub) {
        subscription = sub;
      } else if (sub) {
        // Component unmounted before subscription was created
        sub.unsubscribe();
      }
    });

    // Cleanup subscription on unmount
    return () => {
      isMounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [isLoggedIn, user?.id, authLoading]);

  // Account type is now always fiat - no toggle needed

  // Return balances with real-time updates and manual refresh
  return {
    balances,
    loading,
    error,
    refreshBalances,
  };
};
